import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { debounce } from 'lodash';
import {
  TitleMedium,
  TitleSmall,
  Heading5Text,
  ParagraphMediumText,
} from '../../../components/Texts';
import TextInput from '../../../components/Inputs/Text';
import Button from '../../../components/Button';
import {
  useGetGiftCardCatalogueQuery,
  useGetGiftCardByIdQuery,
} from '../../../redux/api/public/globetopper';
import { getCurrencySymbol } from '../../../common/currency';
import {
  selectGiftCardById,
  selectSelectedCountryCode,
} from '../../../redux/slices/giftCardCatalogue';
import {
  useInitiatePurchaseMutation,
  useBroadcastAndCreateMutation,
  useEstimatePaymentQuery,
} from '../../../redux/api/authorized/globetopper';
import { useSolana, fromTokenAmount } from '../../../hooks/useSolana';
import { useGetCurrentUserProfileQuery } from '../../../redux/api/authorized/auth';
import { selectPrivyToken } from '../../../redux/slices/auth';
import { PHONE_MEDIA_QUERY } from '../../../common/responsive';
import ScrollRestore from '../../../components/ScrollRestore';
import { NavigationBackButton } from '../../../components/BackButton/NavigationBackButton';
import PaymentMethodSelector from '../../../components/PaymentMethodSelector';
import {
  sendEvent,
  sendMetaEvent,
  EVENT_SPEND_CHECKOUT_PAYMENT_TOKEN_CHANGED,
  EVENT_SPEND_CHECKOUT_PURCHASE_BUTTON_CLICKED,
  EVENT_SPEND_CHECKOUT_PURCHASE_COMPLETED,
  EVENT_SPEND_CHECKOUT_PURCHASE_FAILED,
} from '../../../common/analytics';

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const DemoContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentRow = styled.div`
  display: flex;
  width: 100%;
  gap: 48px;
  color: ${({ theme }) => theme.text.regular};
  flex-direction: column;
`;

const TwoColumnContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  flex-direction: row;
  @media ${PHONE_MEDIA_QUERY} {
    flex-direction: column;
    gap: 20px;
  }
`;

const PaymentSection = styled.div`
  width: 100%;
  flex: 1;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const SummarySection = styled.div`
  width: 100%;
  flex: 1;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  flex: 1;

  @media ${PHONE_MEDIA_QUERY} {
    gap: 6px;
  }
`;

const SectionTitle = styled(TitleMedium)`
  margin: 0;
`;

const SummaryTitle = styled(Heading5Text)`
  margin: 0;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  margin-bottom: 16px;
`;

const SummaryCard = styled.div`
  background-color: ${({ theme }) => theme.background.primary};
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummaryItemRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.border.secondary};
`;

const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) =>
    theme.background.secondary || theme.background.primary};
  border-radius: 8px;
  overflow: hidden;
`;

const BrandImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SummaryInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const BrandName = styled(TitleMedium)`
  margin: 0;
  flex: 1;
  min-width: 0;
`;

const AmountText = styled(ParagraphMediumText)`
  margin: 0;
  flex-shrink: 0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 16px;
`;

const TotalLabel = styled(TitleSmall)`
  margin: 0;
`;

const TotalAmount = styled(TitleSmall)`
  margin: 0;
`;

const DiscountCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const DiscountInfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
`;

const DiscountErrorText = styled(ParagraphMediumText)`
  margin: 0;
  color: ${({ theme }) => theme.error || '#ff4444'};
`;

const DiscountSuccessText = styled(ParagraphMediumText)`
  margin: 0;
  color: ${({ theme }) => theme.success || '#00aa00'};
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  width: 100%;
`;

const Line = styled.div`
  margin-top: 30px;
  margin-bottom: 30px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

// Helper to get currency symbol is shared in common/currency

// Form validation schema
const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
});

const SpendCheckout = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const privyToken = useSelector(selectPrivyToken);
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  // This ensures we get the card even if it's from a different country than selected
  const { data: cardById, isLoading: isLoadingById } = useGetGiftCardByIdQuery(
    id,
    {
      skip: !id,
    },
  );

  const selectedCountryCode = useSelector(selectSelectedCountryCode);
  const countryCode = selectedCountryCode || '';
  useGetGiftCardCatalogueQuery(countryCode);

  const selectedCard = useSelector((state) =>
    selectGiftCardById(state, countryCode, id),
  );

  const card = cardById || selectedCard;

  const { signAndSerializeTransactionWithWallet, primaryWallet } = useSolana();
  const [initiatePurchase, { isLoading: isInitiating }] =
    useInitiatePurchaseMutation();
  const [broadcastAndCreate, { isLoading: isBroadcasting }] =
    useBroadcastAndCreateMutation();

  // Get amount from URL params or default
  const amountFromUrl = useMemo(() => {
    return searchParams.get('amount');
  }, [searchParams]);

  // Initialize form with useForm
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onBlur',
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
    },
  });

  const formEmail = watch('email');
  const formFirstName = watch('firstName');
  const formLastName = watch('lastName');

  // Update email when currentUser data loads (only once)
  useEffect(() => {
    if (currentUser?.email && !formEmail) {
      setValue('email', currentUser.email);
    }
  }, [currentUser?.email, formEmail, setValue]);

  const [selectedPaymentToken, setSelectedPaymentToken] = useState(null);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] =
    useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [debouncedDiscountCode, setDebouncedDiscountCode] = useState('');

  const isLoading = isInitiating || isBroadcasting;

  const currencySymbol = card
    ? getCurrencySymbol(card?.detailedInfo?.currency?.code || card.currency)
    : '$';
  const numericAmount = useMemo(() => {
    return parseFloat(amountFromUrl) || 0;
  }, [amountFromUrl]);
  const baseTotal = numericAmount; // Always quantity = 1

  const paymentTokenMint =
    selectedPaymentMethodType === 'token' && selectedPaymentToken?.address
      ? selectedPaymentToken.address
      : undefined;

  // Debounce discount code changes
  const debouncedSetDiscountCode = useRef(
    debounce((value) => {
      setDebouncedDiscountCode(value);
    }, 500),
  ).current;

  useEffect(() => {
    debouncedSetDiscountCode(discountCode);
    return () => {
      debouncedSetDiscountCode.cancel();
    };
  }, [discountCode, debouncedSetDiscountCode]);

  // Meta InitiateCheckout: when user has entered checkout (card + amount known)
  useEffect(() => {
    if (!card || !id || numericAmount <= 0) return;
    sendMetaEvent('InitiateCheckout', {
      content_ids: [String(card.id)],
      content_type: 'product',
      content_name: card.brand,
      currency: card?.detailedInfo?.currency?.code || card?.currency || 'USD',
      value: numericAmount,
      num_items: 1,
    });
  }, [id, card?.id, numericAmount]);

  const { data: estimateData, isFetching: isEstimating } =
    useEstimatePaymentQuery(
      {
        giftCardId: card?.id,
        localAmount: numericAmount.toFixed(2),
        ...(paymentTokenMint ? { paymentTokenMint } : {}),
        ...(debouncedDiscountCode
          ? { discountCode: debouncedDiscountCode.trim() }
          : {}),
      },
      {
        skip: !card || !numericAmount,
      },
    );

  const estimateTokenSymbol = selectedPaymentToken?.symbol || 'USDC';
  const estimateTokenDecimals = selectedPaymentToken?.decimals ?? 6;
  const estimateTokenAmountRaw = estimateData?.paymentTokenAmount ?? null;
  const estimateTokenAmount =
    estimateTokenAmountRaw !== null
      ? String(estimateTokenAmountRaw).includes('.')
        ? parseFloat(estimateTokenAmountRaw)
        : fromTokenAmount(estimateTokenAmountRaw, estimateTokenDecimals)
      : null;
  const formattedEstimate =
    estimateTokenAmount !== null && !Number.isNaN(estimateTokenAmount)
      ? estimateTokenAmount.toLocaleString('en-US', {
          maximumFractionDigits: 6,
        })
      : null;

  // Extract discount information from estimate data
  const discountInfo = estimateData?.discountCode || null;
  const discountApplied = discountInfo?.applied || false;
  const discountAmount = discountInfo?.discountAmount || null;
  const discountLocalCurrencyAmount = discountInfo?.localCurrencyAmount || null;
  const discountReason = discountInfo?.reason || null;

  // Calculate total with discount applied
  const total = useMemo(() => {
    if (discountApplied && discountAmount !== null) {
      return Math.max(0, baseTotal - parseFloat(discountLocalCurrencyAmount));
    }
    return baseTotal;
  }, [baseTotal, discountApplied, discountLocalCurrencyAmount]);

  // Form is valid when react-hook-form says it's valid and payment method is selected
  const isFormValid = isValid && selectedPaymentMethodType;

  const handlePaymentMethodSelected = (paymentData) => {
    if (paymentData.type === 'token') {
      setSelectedPaymentMethodType('token');
      if (paymentData.token) {
        setSelectedPaymentToken(paymentData.token);
        sendEvent(EVENT_SPEND_CHECKOUT_PAYMENT_TOKEN_CHANGED, {
          gift_card_id: card?.id,
          gift_card_brand: card?.brand,
          payment_token_symbol: paymentData.token.symbol,
          payment_token_address: paymentData.token.address,
          payment_method_type: 'token',
          amount: total,
          currency: card?.currency,
        });
      }
    } else if (paymentData.type === 'applePay') {
      setSelectedPaymentMethodType('applePay');
      setSelectedPaymentToken(null);
      sendEvent(EVENT_SPEND_CHECKOUT_PAYMENT_TOKEN_CHANGED, {
        gift_card_id: card?.id,
        gift_card_brand: card?.brand,
        payment_method_type: 'applePay',
        amount: total,
        currency: card?.currency,
      });
    }
  };

  const onSubmit = async (data) => {
    if (!selectedPaymentMethodType) {
      toast.error('Please select a payment method');
      return;
    }

    if (!primaryWallet) {
      toast.error('Please connect your wallet');
      return;
    }

    // Track purchase button click
    sendEvent(EVENT_SPEND_CHECKOUT_PURCHASE_BUTTON_CLICKED, {
      gift_card_id: card.id,
      gift_card_brand: card.brand,
      amount: numericAmount,
      quantity: 1,
      total: total,
      currency: card.currency,
      payment_method_type: selectedPaymentMethodType,
      payment_token_symbol: selectedPaymentToken?.symbol || null,
    });

    // Create a single toast that we'll update throughout the process
    const toastId = toast('Preparing purchase...', {
      isLoading: true,
      autoClose: false,
    });

    try {
      // Step 1: Initiate purchase - get serialized transaction
      const initiateResult = await initiatePurchase({
        giftCardId: card.id.toString(),
        email: data.email.trim(),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        localAmount: numericAmount.toFixed(2),
        ...(selectedPaymentMethodType === 'token' &&
        selectedPaymentToken?.address
          ? { paymentToken: selectedPaymentToken.address }
          : {}),
        ...(debouncedDiscountCode
          ? { discountCode: debouncedDiscountCode.trim() }
          : {}),
      }).unwrap();

      if (!initiateResult.serializedTransaction) {
        toast.update(toastId, {
          render: 'Failed to prepare transaction',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        return;
      }

      const signedTransaction = await signAndSerializeTransactionWithWallet(
        initiateResult.serializedTransaction,
        primaryWallet,
      );

      if (!signedTransaction) {
        toast.update(toastId, {
          render: 'Transaction signing cancelled',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        return;
      }

      // Step 3: Broadcast and create gift card
      toast.update(toastId, {
        render: 'Completing purchase...',
        isLoading: true,
        autoClose: false,
      });

      await broadcastAndCreate({
        serializedTransaction: signedTransaction,
        giftCardId: initiateResult.id.toString(),
      }).unwrap();

      // Success!
      toast.update(toastId, {
        render: 'Gift card purchased successfully!',
        type: 'success',
        isLoading: false,
        autoClose: 5000,
      });

      // Track successful purchase
      sendEvent(EVENT_SPEND_CHECKOUT_PURCHASE_COMPLETED, {
        gift_card_id: card.id,
        gift_card_brand: card.brand,
        amount: numericAmount,
        quantity: 1,
        total: total,
        currency: card.currency,
        payment_method_type: selectedPaymentMethodType,
        payment_token_symbol: selectedPaymentToken?.symbol || null,
      });

      const metaCurrency =
        card?.detailedInfo?.currency?.code || card?.currency || 'USD';
      sendMetaEvent('Purchase', {
        content_ids: [String(card.id)],
        content_type: 'product',
        content_name: card.brand,
        currency: metaCurrency,
        value: total,
        num_items: 1,
      });
      sendMetaEvent('CompleteRegistration', {
        content_name: 'gift_card_purchase',
        currency: metaCurrency,
        value: total,
      });

      // Navigate to my gift cards page
      navigate('/app/spend/my-cards');
    } catch (error) {
      console.error('Purchase error:', error);
      const errorMessage =
        error?.data?.message || error?.message || 'Failed to complete purchase';
      toast.update(toastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });

      // Track failed purchase
      sendEvent(EVENT_SPEND_CHECKOUT_PURCHASE_FAILED, {
        gift_card_id: card.id,
        gift_card_brand: card.brand,
        amount: numericAmount,
        quantity: 1,
        total: total,
        currency: card.currency,
        payment_method_type: selectedPaymentMethodType,
        payment_token_symbol: selectedPaymentToken?.symbol || null,
        error_message: errorMessage,
      });
    }
  };

  if (isLoadingById) {
    return (
      <Container>
        <NavigationBackButton onClick={() => navigate(`/app/spend/${id}`)} />
        <TitleMedium style={{ color: theme.text.title }}>
          Loading gift card...
        </TitleMedium>
      </Container>
    );
  }

  // Show error state if card not found
  if (!card) {
    return (
      <Container>
        <NavigationBackButton onClick={() => navigate(`/app/spend/${id}`)} />
        <TitleMedium style={{ color: theme.text.title }}>
          Gift Card Not Found
        </TitleMedium>
        <ParagraphMediumText style={{ color: theme.text.secondary }}>
          The gift card with ID {id} could not be found.
        </ParagraphMediumText>
      </Container>
    );
  }

  return (
    <>
      <ScrollRestore />
      <DemoContainer>
        <Container>
          <NavigationBackButton onClick={() => navigate(`/app/spend/${id}`)} />
          <ContentRow>
            <TwoColumnContainer>
              <PaymentSection>
                <FormContainer>
                  <SectionTitle $variant='text-title'>
                    Payment method
                  </SectionTitle>

                  <PaymentMethodSelector
                    selectedPaymentMethodType={selectedPaymentMethodType}
                    selectedPaymentToken={selectedPaymentToken}
                    onPaymentMethodSelected={handlePaymentMethodSelected}
                    amount={formattedEstimate}
                    targetToken={null}
                    hasBorder={true}
                  />

                  <Line />

                  <SectionTitle $variant='text-title'>
                    Contact Information
                  </SectionTitle>

                  <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label='Email'
                        type='email'
                        placeholder='Enter your email'
                        error={errors?.email?.message}
                        inputMode='email'
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='firstName'
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label='First Name'
                        type='text'
                        placeholder='Enter your first name'
                        error={errors?.firstName?.message}
                        {...field}
                      />
                    )}
                  />

                  <Controller
                    name='lastName'
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label='Last Name'
                        type='text'
                        placeholder='Enter your last name'
                        error={errors?.lastName?.message}
                        {...field}
                      />
                    )}
                  />
                </FormContainer>
              </PaymentSection>

              <SummarySection>
                <SummaryCard>
                  <TitleRow>
                    <SummaryTitle $variant='text-title'>Summary</SummaryTitle>
                  </TitleRow>

                  <SummaryItemRow>
                    {card.card_image && (
                      <ImageContainer>
                        <BrandImage src={card.card_image} alt={card.brand} />
                      </ImageContainer>
                    )}
                    <SummaryInfo>
                      <BrandName $variant='text-title'>{card.brand}</BrandName>
                      <AmountText $variant='text-regular'>
                        {currencySymbol}
                        {numericAmount.toFixed(2)}
                      </AmountText>
                    </SummaryInfo>
                  </SummaryItemRow>

                  <DiscountCodeContainer>
                    <TextInput
                      label='Discount Code'
                      type='text'
                      placeholder='Enter discount code'
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                    />
                    {debouncedDiscountCode && discountInfo && (
                      <DiscountInfoRow>
                        {discountApplied &&
                          discountLocalCurrencyAmount !== null && (
                            <DiscountSuccessText $variant='text-regular'>
                              Discount applied: {currencySymbol}
                              {parseFloat(discountLocalCurrencyAmount).toFixed(
                                2,
                              )}
                            </DiscountSuccessText>
                          )}
                        {!discountApplied && discountReason && (
                          <DiscountErrorText $variant='text-regular'>
                            {discountReason}
                          </DiscountErrorText>
                        )}
                      </DiscountInfoRow>
                    )}
                  </DiscountCodeContainer>

                  <TotalRow>
                    <TotalLabel $variant='text-title'>Total</TotalLabel>
                    <TotalAmount $variant='text-title'>
                      {currencySymbol}
                      {total.toFixed(2)}
                    </TotalAmount>
                  </TotalRow>
                  {formattedEstimate && (
                    <TotalRow>
                      <TotalLabel $variant='text-secondary'>
                        Estimated {estimateTokenSymbol}
                      </TotalLabel>
                      <TotalAmount $variant='text-title'>
                        {formattedEstimate}
                        {isEstimating ? '...' : ''}
                      </TotalAmount>
                    </TotalRow>
                  )}

                  <ButtonContainer>
                    <Button
                      variant='primary'
                      fullWidth
                      onClick={handleSubmit(onSubmit)}
                      loading={isLoading}
                      disabled={isLoading || !isFormValid}
                    >
                      {isLoading ? 'Processing...' : 'Complete Purchase'}
                    </Button>
                  </ButtonContainer>
                </SummaryCard>
              </SummarySection>
            </TwoColumnContainer>
          </ContentRow>
        </Container>
      </DemoContainer>
    </>
  );
};

export default SpendCheckout;
